<template>
  <table class="otp-table">
    <tr>
      <th>
        <button
          class="calendar-btn calendar-btn-prev rounded-circle"
          :class="{
            'bg-white': isMobile,
            'bg-medium-gray': isClinius && isMobile,
          }"
          @click="prevMonth"
          :disabled="!prevWeekPossible"
        >
          <font-awesome-icon icon="angle-double-left" />
        </button>
      </th>
      <th :colspan="daysThisMonth">
        <template v-if="daysThisMonth === 1">{{
          $d(calDays[0], "calendar_month_short")
        }}</template>
        <template v-else>{{ $d(calDays[0], "calendar_month") }}</template>
      </th>
      <th
        v-if="daysThisMonth < anzahlTage"
        :colspan="anzahlTage - daysThisMonth"
      >
        <span class="month-divider"></span>
        <template v-if="daysThisMonth === anzahlTage - 1">{{
          $d(calDays[anzahlTage - 1], "calendar_month_short")
        }}</template>
        <template v-else>{{
          $d(calDays[anzahlTage - 1], "calendar_month")
        }}</template>
      </th>
      <th>
        <button
          class="calendar-btn calendar-btn-next rounded-circle"
          :class="{
            'bg-white': isMobile,
            'bg-medium-gray': isClinius && isMobile,
          }"
          @click="nextMonth"
          :disabled="!nextWeekPossible"
        >
          <font-awesome-icon icon="angle-double-right" />
        </button>
      </th>
    </tr>
    <tr>
      <th>
        <button
          class="calendar-btn calendar-btn-prev rounded-circle"
          :class="{
            'bg-white': isMobile,
            'bg-medium-gray': isClinius && isMobile,
          }"
          @click="prevWeek"
          :disabled="!prevWeekPossible"
        >
          <font-awesome-icon icon="angle-left" />
        </button>
      </th>
      <th v-for="(d, i) in calDays" :key="d.getTime()">
        <span v-if="d.getDate() == 1 && i != 0" class="month-divider"></span>
        <template v-if="isMobile">
          <div>{{ $d(d, "calendar_head_mobile_1") }}.</div>
          <div class="fs-12 font-weight-normal">
            {{ $d(d, "calendar_head_mobile_2") }}
          </div>
        </template>
        <template v-else
          >{{ $d(d, "calendar_head_1") }}.
          {{ $d(d, "calendar_head_2") }}</template
        >
      </th>
      <th>
        <button
          class="calendar-btn-next calendar-btn rounded-circle"
          :class="{
            'bg-white': isMobile,
            'bg-medium-gray': isClinius && isMobile,
          }"
          @click="nextWeek"
          :disabled="!nextWeekPossible"
        >
          <font-awesome-icon icon="angle-right" />
        </button>
      </th>
    </tr>
    <tr v-if="$store.getters.isLoading">
      <td :colspan="anzahlTage + 2" class="pt-5">
        <font-awesome-icon icon="spinner" spin size="2x" />
      </td>
    </tr>
    <!-- Wir brauchen das 0-Basiert, v-for startet aber mit 1, deswegen nutzen wir hier den index-->
    <tr v-else v-for="(_, r) in anzZeilen" :key="r">
      <td></td>
      <td v-for="(d, i) in calDays" :key="d.getTime()">
        <span v-if="d.getDate() == 1 && i != 0" class="month-divider"></span>
        <div
          v-if="termineAtDay(d, r, behandler)"
          :class="[
            'cal-termin fs-14',
            { selected: d == selectedDay && r == selectedRow },
            {
              optimum:
                isHager && termineAtDay(d, r, behandler).proposals[0].preferred,
            },
            {
              'bg-medium-gray':
                isClinius &&
                isMobile &&
                !(d == selectedDay && r == selectedRow),
            },
          ]"
          @click="selectTermineAtDay(d, r)"
        >
          {{ termineAtDay(d, r, behandler).time }}

          <span
            class="preferred-icon"
            v-if="isClinius && termineAtDay(d, r, behandler).preferred"
          >
            <font-awesome-icon icon="star" class="outer-star" />
            <font-awesome-icon icon="star" class="inner-star" />
          </span>

        </div>

        <div v-else-if="r == termineAtDayList(d, true).length && d >= inThreeDays && !showAllAtDays.includes(d) && termineAtDayList(d, false).length - termineAtDayList(d, true).length > 0">
          <button
            class="calendar-btn-more rounded-circle"
            :class="{
              'bg-white': isMobile,
              'bg-medium-gray': isClinius && isMobile,
            }"
            @click="showAllAtDay(d)"
          >
            <!--<font-awesome-icon icon="plus" />-->
            +
            <span class="badge">{{termineAtDayList(d, false).length - termineAtDayList(d, true).length}}</span>
          </button>
        </div>

        <div v-else class="no-termin">-</div>
      </td>
      <td></td>
    </tr>
  </table>
</template>

<script>
import {
  SET_SELECTED_DAY,
  SET_SELECTED_ROW,
  SET_SELECTED_TERMIN,
  SET_BEHANDLERAUSWAHL_UEBERSPRUNGEN,
  SET_START_DATE,
} from "@/store/mutations.type";
import { SET_SELECTED_PROPOSALS } from '../store/mutations.type';
import { DateTime } from "luxon";

const millisPerDay = 1000 * 60 * 60 * 24;

export default {
  name: "CalendarComponent",
  data() {
    return {
      showAllAtDays: []
    };
  },
  props: {
    anzahlTage: { type: Number, default: 7 },
    vorverlegt: { type: Boolean, default: false}
  },
  computed: {
    anzZeilen() {
      var cnt = 7;
      this.calDays.forEach(date => {
        let preferredOnly = this.isClinius && date >= this.inThreeDays && !this.showAllAtDays.includes(date)
        cnt = Math.max(cnt, this.termineAtDayList(date, preferredOnly).length)
      });
      return cnt;
    },
    minCount() {
      return this.$store.state.termin.behandlung.min_count || 4;
    },
    calDays() {
      var days = [];
      for (var i = 0; i < this.anzahlTage; i++) {
        days.push(new Date(this.startDate.getTime() + i * 24 * 60 * 60 * 1000));
      }
      return days;
    },
    daysThisMonth() {
      return this.calDays.filter(
        (d) => d.getMonth() == this.startDate.getMonth()
      ).length;
    },
    selectedDay: {
      get() {
        return this.$store.state.termin.selectedDay;
      },
      set(val) {
        this.$store.commit(`termin/${SET_SELECTED_DAY}`, val);
      },
    },
    selectedRow: {
      get() {
        return this.$store.state.termin.selectedRow;
      },
      set(val) {
        this.$store.commit(`termin/${SET_SELECTED_ROW}`, val);
      },
    },
    selectedProposals: {
      get() {
        return this.$store.state.termin.selectedProposals;
      },
      set(val) {
        this.$store.commit(`termin/${SET_SELECTED_PROPOSALS}`, val);
      },
    },
    startDate: {
      get() {
        return this.$store.state.startDate;
      },
      set(val) {
        this.$store.commit(`${SET_START_DATE}`, val);
      },
    },
    endDate() {
      return this.calDays[this.anzahlTage - 1];
    },
    prevWeekPossible() {
      if (this.startDate <= DateTime.now().startOf('day').toJSDate() || this.minDate && this.startDate <= this.minDate) return false
      return true
    },
    nextWeekPossible() {
      if (this.maxDate && this.endDate >= this.maxDate) return false
      return true
    },
    selectedTermin: {
      get() {
        return this.$store.state.termin.selectedTermin;
      },
      set(val) {
        this.$store.commit(`termin/${SET_SELECTED_TERMIN}`, val);
      },
    },
    behandler: {
      get() {
        return this.$store.state.termin.behandler;
      },
    },
    inThreeDays() {
      return new Date((Math.floor(new Date().getTime() / millisPerDay) * millisPerDay) + 3 * millisPerDay);
    },
    minDate() {
        if (!this.$store.state.termin.actualTermin?.min_date) return null;
        return DateTime.fromISO(this.$store.state.termin.actualTermin.min_date).toJSDate();
    },
    maxDate() {
      if (!this.$store.state.termin.actualTermin?.max_date) return null;
      return DateTime.fromISO(this.$store.state.termin.actualTermin.max_date).toJSDate();
    },
  },
  methods: {
    prevWeek() {
      this.setStartDate(
        new Date(
          this.startDate.getTime() - this.anzahlTage * millisPerDay
        )
      );
    },
    nextWeek() {
      this.setStartDate(
        new Date(
          this.startDate.getTime() + this.anzahlTage * millisPerDay
        )
      );
    },
    prevMonth() {
      this.setStartDate(
        new Date(
          this.calDays[0].getFullYear(),
          this.calDays[0].getMonth() - 1,
          1
        )
      );
    },
    nextMonth() {
      this.setStartDate(
        new Date(
          this.calDays[this.anzahlTage - 1].getFullYear(),
          this.calDays[0].getMonth() + 1,
          1
        )
      );
    },
    setStartDate(date) {
      if (this.minDate && date < this.minDate) {
        date = this.minDate
      }
      if (this.maxDate && date > this.maxDate) {
        date = this.maxDate
      }
      if (date < DateTime.now().startOf('day').toJSDate()) {
        date = DateTime.now().startOf('day').toJSDate()
      }
      this.startDate = date;
      this.$emit("dateChanged", this.startDate);
    },
    termineAtDayList(date, preferredOnly = false) {
      let ts = this.$store.getters.termineAtDay(date, this.vorverlegt);

      if (!ts) return null;

      let out = Object.keys(ts).map((tsk) => {
        let proposals = ts[tsk];
        let hasPreferred = proposals.some(t => t.preferred);
        return { time: tsk, proposals: proposals, preferred: hasPreferred };
      })

      if (this.isClinius) {
        out.sort((a,b) => {
          if (a.preferred != b.preferred)
            return (b.preferred ? 1 : 0) - (a.preferred ? 1 : 0)
          return a.time.localeCompare(b.time)
        })
      }

      if (preferredOnly) {
        out = out.filter((t,i) => t.preferred || i < this.minCount)
      }

      return out
    },
    termineAtDay(date, i) {
      let preferredOnly = this.isClinius && date >= this.inThreeDays && !this.showAllAtDays.includes(date);

      let tsk = this.termineAtDayList(date, preferredOnly);
      if (i >= tsk.length) return null;
      return tsk[i];
    },
    selectTermineAtDay(d, r) {
      let termine = this.termineAtDay(d, r);
      if (termine.proposals.length === 1) {
        this.selectedTermin = termine.proposals[0];
        this.$store.commit(
          `termin/${SET_BEHANDLERAUSWAHL_UEBERSPRUNGEN}`,
          true
        );
      }
      this.selectedDay = d;
      this.selectedRow = r;
      /*
      let tsk = Object.keys(termine);

      return { time: tsk[i], proposals: ts[tsk[i]] };
      */
      this.selectedProposals = termine;
    },
    showAllAtDay(d) {
      if (this.showAllAtDays.includes(d)) return
      this.showAllAtDays.push(d);
    }
  },
};
</script>
